import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Loader2, Plus, ChevronLeft, ChevronRight, ShieldCheck, ShieldAlert } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { http, apiError, DIVISIONS, STATUSES, SOURCES, CONDITIONS, imageUrl, formatDateTime } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const EMPTY = { title: "", division: "truck", category: "", make: "", model: "", year: "", price: "", location: "", condition: "", usage: "",
  specs: [], description: "", images: [], status: "available", source: "unspecified", stock_number: "", featured: false, published: false, verified: false, verification_note: "" };

const F = ({ label, children, className = "" }) => <div className={className}><label className="label">{label}</label>{children}</div>;

const Verification = ({ a, setA, owner }) => {
  const toggle = (e) => setA({ ...a, verified: e.target.checked, ...(e.target.checked ? {} : { published: false, featured: false }) });
  return (
    <div className="border-t hairline pt-5 space-y-3" data-testid="asset-verification">
      <p className={`flex items-center gap-2 text-sm ${a.verified ? "text-emerald-300" : "text-amber-200"}`} data-testid="asset-verification-status">
        {a.verified ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
        {a.verified ? `Verified${a.verified_by ? ` by ${a.verified_by}` : ""}${a.verified_at ? ` · ${formatDateTime(a.verified_at)}` : ""}` : "Not verified — cannot be published"}
      </p>
      {owner ? (
        <>
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm"><span className="block text-slate-100">I have verified this asset</span><span className="text-xs text-slate-400">Real asset, accurate details, photos of this exact unit</span></span>
            <input type="checkbox" checked={a.verified} onChange={toggle} className="w-5 h-5 accent-[#d4af37]" data-testid="asset-verified-checkbox" />
          </label>
          <textarea rows={2} className="field !text-xs" placeholder="Verification notes (internal), e.g. title checked, inspected on site" value={a.verification_note || ""} onChange={(e) => setA({ ...a, verification_note: e.target.value })} data-testid="asset-verification-note" />
        </>
      ) : <p className="text-xs text-slate-400">Only Scott can verify listings. Changing key details of a verified listing sends it back for verification.</p>}
    </div>
  );
};

export default function AssetEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { user } = useAuth();
  const owner = user?.role === "owner";
  const [a, setA] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const div = DIVISIONS.find((d) => d.key === a.division);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  useEffect(() => {
    if (isNew) return undefined;
    http.get(`/admin/assets/${id}`)
      .then(({ data }) => setA({ ...EMPTY, ...data, year: data.year ?? "", price: data.price ?? "" }))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
    return undefined;
  }, [id, isNew]);

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await http.post("/admin/upload", fd);
      setA((p) => ({ ...p, images: [...p.images, ...(data?.paths || [])] }));
      toast.success(`${data?.paths?.length || 0} image(s) uploaded`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const moveImg = (i, d) => {
    const imgs = [...a.images];
    const j = i + d;
    if (j < 0 || j >= imgs.length) return;
    [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
    setA({ ...a, images: imgs });
  };
  const setSpec = (i, k, v) => setA({ ...a, specs: a.specs.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...a, year: a.year === "" ? null : Number(a.year), price: a.price === "" ? null : Number(a.price),
      specs: a.specs.filter((s) => s.label.trim() && s.value.trim()) };
    try {
      const { data } = isNew ? await http.post("/admin/assets", body) : await http.put(`/admin/assets/${id}`, body);
      toast.success(isNew ? "Asset created" : "Asset saved");
      if (!owner && a.verified && !data?.verified) toast.info("Details changed, so the listing was unpublished until Scott verifies it again.");
      qc.invalidateQueries();
      nav("/admin/inventory");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-gold" />;

  return (
    <form onSubmit={save} data-testid="asset-editor">
      <Link to="/admin/inventory" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold mb-6"><ArrowLeft className="w-4 h-4" /> All inventory</Link>
      <AdminTitle title={isNew ? "Add asset" : "Edit asset"}>
        <button type="submit" className="btn-gold" disabled={saving || uploading} data-testid="asset-save-button">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save listing</button>
      </AdminTitle>
      <div className="grid xl:grid-cols-3 gap-8">
        <div className="xl:col-span-2 space-y-8">
          <section className="border hairline p-6 bg-[var(--ink-2)] grid sm:grid-cols-2 gap-5">
            <F label="Asset title *" className="sm:col-span-2"><input required className="field" value={a.title} onChange={set("title")} placeholder="e.g. 2021 Freightliner Cascadia 126 Sleeper" data-testid="asset-title-input" /></F>
            <F label="Division *">
              <select className="field" value={a.division} onChange={(e) => setA({ ...a, division: e.target.value, category: "" })} data-testid="asset-division-select">
                {DIVISIONS.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
              </select>
            </F>
            <F label="Asset category">
              <select className="field" value={a.category} onChange={set("category")} data-testid="asset-category-select">
                <option value="">Select…</option>
                {div?.categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Make"><input className="field" value={a.make} onChange={set("make")} data-testid="asset-make-input" /></F>
            <F label="Model"><input className="field" value={a.model} onChange={set("model")} data-testid="asset-model-input" /></F>
            <F label="Year"><input type="number" className="field" value={a.year} onChange={set("year")} data-testid="asset-year-input" /></F>
            <F label="Price (USD) — leave blank for “Request Price”"><input type="number" min="0" className="field" value={a.price} onChange={set("price")} data-testid="asset-price-input" /></F>
            <F label="Location"><input className="field" value={a.location} onChange={set("location")} placeholder="City, ST" data-testid="asset-location-input" /></F>
            <F label="Condition">
              <select className="field" value={a.condition} onChange={set("condition")} data-testid="asset-condition-select">
                <option value="">Select…</option>
                {Array.from(new Set([...CONDITIONS, a.condition].filter(Boolean))).map((c) => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label={`${div?.usage || "Mileage / hours"} (where applicable)`}><input className="field" value={a.usage} onChange={set("usage")} placeholder="e.g. 412,000 mi or 3,200 hrs" data-testid="asset-usage-input" /></F>
            <F label="Stock / reference # (auto if blank)"><input className="field font-mono" value={a.stock_number} onChange={set("stock_number")} data-testid="asset-stock-input" /></F>
            <F label="Description" className="sm:col-span-2"><textarea rows={6} className="field resize-y" value={a.description} onChange={set("description")} data-testid="asset-description-input" /></F>
          </section>

          <section className="border hairline p-6 bg-[var(--ink-2)]">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Specifications</p>
              <button type="button" onClick={() => setA({ ...a, specs: [...a.specs, { label: "", value: "" }] })} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="add-spec-button"><Plus className="w-3 h-3" /> Add spec</button>
            </div>
            {!a.specs.length && <p className="text-sm text-slate-400">e.g. Engine, Transmission, Length, Axles, Operating weight.</p>}
            <div className="space-y-2">
              {a.specs.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.4fr_auto] gap-2">
                  <input className="field !py-2.5" placeholder="Label" value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} data-testid={`spec-label-${i}`} />
                  <input className="field !py-2.5" placeholder="Value" value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} data-testid={`spec-value-${i}`} />
                  <button type="button" onClick={() => setA({ ...a, specs: a.specs.filter((_, idx) => idx !== i) })} className="w-10 grid place-items-center border border-white/15 hover:border-rose-400" data-testid={`spec-remove-${i}`}><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </section>

          <section className="border hairline p-6 bg-[var(--ink-2)]">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Images <span className="text-slate-400 normal-case tracking-normal">(first image is the cover)</span></p>
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-2 !px-3 !text-xs" disabled={uploading} data-testid="upload-images-button">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload images
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="image-file-input" />
            </div>
            {!a.images.length ? (
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-white/20 py-12 text-sm text-slate-300 hover:border-[var(--gold)]">Select one or more photos (JPG, PNG, WEBP; up to 15 MB each)</button>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="asset-image-list">
                {a.images.map((src, i) => (
                  <div key={src} className="relative group aspect-[4/3] bg-[var(--ink-3)] overflow-hidden border hairline">
                    <img src={imageUrl(src)} alt="" className="w-full h-full object-cover" />
                    {i === 0 && <span className="absolute top-1 left-1 font-mono text-[0.6rem] bg-[var(--gold)] text-[#0a0d14] px-1.5 py-0.5">COVER</span>}
                    <div className="absolute inset-x-0 bottom-0 flex justify-between bg-[#0a0d14]/80 p-1">
                      <button type="button" onClick={() => moveImg(i, -1)} className="p-1 hover:text-gold"><ChevronLeft className="w-4 h-4" /></button>
                      <button type="button" onClick={() => setA({ ...a, images: a.images.filter((x) => x !== src) })} className="p-1 hover:text-rose-300" data-testid={`remove-image-${i}`}><X className="w-4 h-4" /></button>
                      <button type="button" onClick={() => moveImg(i, 1)} className="p-1 hover:text-gold"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-6">
          <section className="border hairline p-6 bg-[var(--ink-2)] space-y-5 xl:sticky xl:top-6">
            <F label="Availability status">
              <select className="field" value={a.status} onChange={(e) => setA({ ...a, status: e.target.value, featured: e.target.value === "sold" ? false : a.featured })} data-testid="asset-status-select">
                {Object.entries(STATUSES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
              </select>
            </F>
            <p className="text-xs text-slate-400">“Sold” removes the listing from public inventory.</p>
            <F label="Ownership / listing source">
              <select className="field" value={a.source} onChange={set("source")} data-testid="asset-source-select">
                {Object.entries(SOURCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
            <p className="text-xs text-slate-400">Only choose “Owned by Selby Asset Group” when the company actually owns the asset.</p>
            <Verification a={a} setA={setA} owner={owner} />
            <label className={`flex items-center justify-between gap-3 border-t hairline pt-5 ${a.verified ? "cursor-pointer" : "opacity-50"}`}>
              <span className="text-sm"><span className="block text-slate-100">Published</span><span className="text-xs text-slate-400">{a.verified ? "Visible on the website" : "Available once the asset is verified"}</span></span>
              <input type="checkbox" checked={a.published} disabled={!a.verified} onChange={set("published")} className="w-5 h-5 accent-[#d4af37]" data-testid="asset-published-checkbox" />
            </label>
            <label className={`flex items-center justify-between gap-3 border-t hairline pt-5 ${a.verified ? "cursor-pointer" : "opacity-50"}`}>
              <span className="text-sm"><span className="block text-slate-100">Feature on homepage</span><span className="text-xs text-slate-400">Shows in the Featured tab</span></span>
              <input type="checkbox" checked={a.featured} disabled={a.status === "sold" || !a.verified} onChange={set("featured")} className="w-5 h-5 accent-[#d4af37]" data-testid="asset-featured-checkbox" />
            </label>
            <button type="submit" className="btn-gold w-full" disabled={saving || uploading} data-testid="asset-save-button-side">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save listing</button>
          </section>
        </aside>
      </div>
    </form>
  );
}
